import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

function InvoiceSettings() {
    const { authFetch } = useAuth();
    const [settings, setSettings] = useState({
        invoice_company_name: '',
        invoice_address: '',
        invoice_contact: '',
        invoice_bank_holder: '',
        invoice_iban: '',
        invoice_bic: '',
        invoice_footer: '',
        invoice_tax_note: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await authFetch('/api/settings/invoice');
            const data = await response.json();
            if (data.data) {
                setSettings(prev => ({ ...prev, ...data.data }));
            }
        } catch (err) {
            console.error('Fehler beim Laden der Einstellungen:', err);
            setMessage({ type: 'error', text: 'Fehler beim Laden der Einstellungen' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);

        try {
            const response = await authFetch('/api/settings', {
                method: 'PUT',
                body: settings
            });
            const data = await response.json();

            if (data.message === 'success') {
                setMessage({ type: 'success', text: 'Einstellungen gespeichert!' });
            } else {
                setMessage({ type: 'error', text: data.error || 'Fehler beim Speichern' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Netzwerkfehler beim Speichern' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center py-4">Laden...</div>;
    }

    return (
        <div>
            <h3>Rechnungseinstellungen</h3>
            <p className="text-muted mb-4">
                Hier kannst du die Daten anpassen, die auf den Rechnungs-PDFs erscheinen.
            </p>

            {message && (
                <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible`}>
                    {message.text}
                    <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
                </div>
            )}

            <div className="row">
                <div className="col-md-6">
                    <div className="card mb-4">
                        <div className="card-header">
                            <strong>Firmendaten</strong>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label">Firmenname</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.invoice_company_name}
                                    onChange={(e) => handleChange('invoice_company_name', e.target.value)}
                                    placeholder="z.B. Futurhinos for Kids"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Adresse</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.invoice_address}
                                    onChange={(e) => handleChange('invoice_address', e.target.value)}
                                    placeholder="z.B. Rheinstraße 4 | 73614 Schorndorf"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Kontakt (E-Mail & Website)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.invoice_contact}
                                    onChange={(e) => handleChange('invoice_contact', e.target.value)}
                                    placeholder="z.B. info@futurhinos.de | www.futurhinos.de"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="card mb-4">
                        <div className="card-header">
                            <strong>Bankverbindung</strong>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="form-label">Kontoinhaber</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.invoice_bank_holder}
                                    onChange={(e) => handleChange('invoice_bank_holder', e.target.value)}
                                    placeholder="z.B. Futurhinos"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">IBAN</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.invoice_iban}
                                    onChange={(e) => handleChange('invoice_iban', e.target.value)}
                                    placeholder="z.B. DE66 5001 0517 6000 5878 84"
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">BIC</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={settings.invoice_bic}
                                    onChange={(e) => handleChange('invoice_bic', e.target.value)}
                                    placeholder="z.B. INGDDEFFXXX"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-header">
                    <strong>Zusätzliche Einstellungen</strong>
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label">Steuerhinweis (optional)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={settings.invoice_tax_note}
                            onChange={(e) => handleChange('invoice_tax_note', e.target.value)}
                            placeholder="z.B. Enthält 19% MwSt. oder leer lassen"
                        />
                        <small className="text-muted">
                            Wird im Footer der Rechnung angezeigt. Leer lassen wenn kein Steuerhinweis gewünscht.
                        </small>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Footer-Text (optional)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={settings.invoice_footer}
                            onChange={(e) => handleChange('invoice_footer', e.target.value)}
                            placeholder="z.B. Vielen Dank für Ihr Vertrauen!"
                        />
                        <small className="text-muted">Zusätzlicher Text unten auf der Rechnung</small>
                    </div>
                </div>
            </div>

            <button
                className="btn btn-primary btn-lg"
                onClick={handleSave}
                disabled={saving}
            >
                {saving ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Speichern...
                    </>
                ) : (
                    'Einstellungen speichern'
                )}
            </button>
        </div>
    );
}

export default InvoiceSettings;
